import { createSlice, createAction, PayloadAction } from "@reduxjs/toolkit";
import { OrgInfo } from "@/features/auth/services/AuthService";

// Actions
export const fetchOrganizations = createAction(
  "organizations/fetchOrganizations"
);
export const fetchOrganizationsSuccess = createAction<OrgInfo[]>(
  "organizations/fetchOrganizationsSuccess"
);
export const fetchOrganizationsFailure = createAction<string>(
  "organizations/fetchOrganizationsFailure"
);
export const setSelectedOrganization = createAction<string | null>(
  "organizations/setSelectedOrganization"
);

// State interface
interface OrganizationsState {
  organizations: OrgInfo[];
  selectedOrganizationId: string | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: OrganizationsState = {
  organizations: [],
  selectedOrganizationId: null,
  loading: false,
  error: null,
};

// Slice
const organizationsSlice = createSlice({
  name: "organizations",
  initialState,
  reducers: {
    setSelectedOrganization: (state, action: PayloadAction<string | null>) => {
      state.selectedOrganizationId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizations, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchOrganizationsSuccess,
        (state, action: PayloadAction<OrgInfo[]>) => {
          state.loading = false;
          state.organizations = action.payload;
          state.error = null;
        }
      )
      .addCase(
        fetchOrganizationsFailure,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

// Export actions
export const { setSelectedOrganization: setSelectedOrganizationAction } =
  organizationsSlice.actions;

// Selectors
export const selectOrganizations = (state: {
  organizations: OrganizationsState;
}) => state.organizations.organizations;

export const selectOrganizationsLoading = (state: {
  organizations: OrganizationsState;
}) => state.organizations.loading;

export const selectOrganizationsError = (state: {
  organizations: OrganizationsState;
}) => state.organizations.error;

export const selectSelectedOrganizationId = (state: {
  organizations: OrganizationsState;
}) => state.organizations.selectedOrganizationId;

export const selectSelectedOrganization = (state: {
  organizations: OrganizationsState;
}) => {
  const selectedId = state.organizations.selectedOrganizationId;
  return selectedId
    ? state.organizations.organizations.find((org) => org.guid === selectedId)
    : null;
};

export default organizationsSlice.reducer;
